import { Contract } from '@starx/shared-types';

export function renderContractReceipt(contract: Contract, timeZone?: string): string {
  const isDemo = contract.contract_id.startsWith('DEMO-');
  const isWin = contract.status === 'SETTLED_WIN';
  const isLoss = contract.status === 'SETTLED_LOSS';
  const isTie = contract.status === 'SETTLED_TIE';
  const isCashout = contract.status === 'SETTLED_CASHOUT';

  const stakeNum = parseFloat(String(contract.stake || 0));
  const payoutNum = parseFloat(String(contract.actual_payout || 0));
  const isRiskFree = Boolean(contract.is_risk_free || (contract as any).risk_free_token_id);
  const refundAmount = parseFloat(String((contract as any).risk_free_refund_amount || 0));
  const maxRefund = parseFloat(String(contract.risk_free_max_refund || 0));
  const effectiveRefund = isRiskFree && isLoss ? (refundAmount > 0 ? refundAmount : Math.min(stakeNum, maxRefund)) : 0;

  let badgeColor = isWin ? '#10b981' : isLoss ? '#f43f5e' : (isTie || isCashout) ? '#38bdf8' : '#f59e0b';
  let statusBadgeText = contract.status;

  let netProfitStr = `${contract.currency} 0.00`;
  if (isWin) {
    const netProfit = payoutNum - stakeNum;
    netProfitStr = `+${contract.currency} ${netProfit.toFixed(2)}`;
  } else if (isCashout) {
    netProfitStr = `+${contract.currency} ${payoutNum.toFixed(2)}`;
  } else if (isTie) {
    netProfitStr = `${contract.currency} 0.00`;
  } else if (isLoss) {
    if (effectiveRefund > 0) {
      if (effectiveRefund >= stakeNum) {
        netProfitStr = `±${contract.currency} 0.00 (Refunded)`;
        badgeColor = '#10b981';
        statusBadgeText = 'SETTLED_LOSS (PROTECTED)';
      } else {
        const netLoss = stakeNum - effectiveRefund;
        netProfitStr = `-${contract.currency} ${netLoss.toFixed(2)} (Protected)`;
      }
    } else {
      netProfitStr = `-${contract.currency} ${stakeNum.toFixed(2)}`;
    }
  }

  const headerTitle = isDemo ? 'PRACTICE DEMO RECEIPT' : 'OFFICIAL TRADE RECEIPT';
  const timeOptions: Intl.DateTimeFormatOptions = { hour12: false, ...(timeZone ? { timeZone } : {}) };
  const openedAt = (contract.entry_timestamp || contract.created_at) ? new Date(contract.entry_timestamp || contract.created_at).toLocaleTimeString([], timeOptions) : '—';
  const closedAt = (contract.expiry_timestamp || contract.settlement_timestamp || contract.settled_at) ? new Date(contract.expiry_timestamp || contract.settlement_timestamp || contract.settled_at).toLocaleTimeString([], timeOptions) : '—';

  return `
    <div class="contract-receipt-modal" style="background: #121824; border: 1px solid #1e293b; border-radius: 12px; padding: 24px; max-width: 520px; width: 100%; color: #f8fafc; font-family: 'Inter', sans-serif; box-shadow: 0 20px 50px rgba(0,0,0,0.5);">
      <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #1e293b; padding-bottom: 16px; margin-bottom: 16px;">
        <div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 11px; text-transform: uppercase; color: ${isDemo ? '#eab308' : '#38bdf8'}; font-weight: 700; letter-spacing: 0.05em;">${headerTitle}</span>
            ${isRiskFree ? `<span style="background: rgba(16,185,129,0.18); color: #34d399; border: 1px solid rgba(16,185,129,0.35); padding: 2px 7px; border-radius: 4px; font-size: 10px; font-weight: 700;">🛡️ 100% PROTECTED</span>` : ''}
          </div>
          <h3 style="margin: 4px 0 0 0; font-size: 18px; color: #ffffff; display: flex; align-items: center; gap: 6px;">
            ${contract.symbol} <span style="color: ${contract.direction === 'CALL' ? '#10b981' : '#f43f5e'};">${contract.direction === 'CALL' ? 'CALL ▲' : 'PUT ▼'}</span>
          </h3>
        </div>
        <span style="background: ${badgeColor}22; color: ${badgeColor}; border: 1px solid ${badgeColor}44; padding: 6px 14px; border-radius: 9999px; font-weight: 800; font-size: 12px; letter-spacing: 0.02em;">
          ${statusBadgeText}
        </span>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 13px; margin-bottom: 16px;">
        <div style="background: #0b0e14; padding: 10px 14px; border-radius: 8px;">
          <span style="color: #64748b; font-size: 11px; display: block;">Contract ID</span>
          <code style="font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #38bdf8;">${contract.contract_id}</code>
        </div>
        <div style="background: #0b0e14; padding: 10px 14px; border-radius: 8px;">
          <span style="color: #64748b; font-size: 11px; display: block;">Net Outcome</span>
          <strong style="font-size: 14px; color: ${badgeColor}; font-family: 'JetBrains Mono', monospace;">${netProfitStr}</strong>
        </div>
        <div style="background: #0b0e14; padding: 10px 14px; border-radius: 8px;">
          <span style="color: #64748b; font-size: 11px; display: block;">Stake Amount</span>
          <strong style="font-size: 15px; color: #f8fafc;">${contract.currency} ${stakeNum.toFixed(2)}</strong>
        </div>
        <div style="background: #0b0e14; padding: 10px 14px; border-radius: 8px;">
          <span style="color: #64748b; font-size: 11px; display: block;">Total Return</span>
          <strong style="font-size: 15px; color: ${badgeColor};">${contract.currency} ${payoutNum.toFixed(2)}</strong>
        </div>

        ${effectiveRefund > 0 ? `
          <div style="grid-column: span 2; background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.3); padding: 10px 14px; border-radius: 8px; display: flex; justify-content: space-between; align-items: center;">
            <div>
              <span style="color: #34d399; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 4px;">🛡️ Risk-Free Protection Refund</span>
              <span style="color: #94a3b8; font-size: 11px;">Credited back directly to your balance</span>
            </div>
            <strong style="color: #34d399; font-size: 14px; font-family: 'JetBrains Mono', monospace;">+${contract.currency} ${effectiveRefund.toFixed(2)}</strong>
          </div>
        ` : ''}

        <div style="background: #0b0e14; padding: 10px 14px; border-radius: 8px;">
          <span style="color: #64748b; font-size: 11px; display: block;">Strike Price</span>
          <span style="font-family: 'JetBrains Mono', monospace; font-size: 13px; color: #f8fafc;">${contract.strike_price ? parseFloat(String(contract.strike_price)).toFixed(5) : '0.00000'}</span>
        </div>
        <div style="background: #0b0e14; padding: 10px 14px; border-radius: 8px;">
          <span style="color: #64748b; font-size: 11px; display: block;">Settlement Price</span>
          <span style="font-family: 'JetBrains Mono', monospace; font-size: 13px; color: #f8fafc;">${contract.expiry_price ? parseFloat(String(contract.expiry_price)).toFixed(5) : 'N/A'}</span>
        </div>
        <div style="background: #0b0e14; padding: 10px 14px; border-radius: 8px;">
          <span style="color: #64748b; font-size: 11px; display: block;">Opened At</span>
          <span style="font-size: 12px; color: #94a3b8;">${openedAt}</span>
        </div>
        <div style="background: #0b0e14; padding: 10px 14px; border-radius: 8px;">
          <span style="color: #64748b; font-size: 11px; display: block;">Settled At</span>
          <span style="font-size: 12px; color: #94a3b8;">${closedAt}</span>
        </div>
      </div>

      ${!isDemo && (contract.settlement_evidence_hash || (contract as any).evidence_hash || (contract as any).settlement_signature) ? `
        <details style="background: #0b0e14; border: 1px solid #1e293b; padding: 10px 14px; border-radius: 8px; margin-bottom: 12px; cursor: pointer;">
          <summary style="font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; outline: none; user-select: none;">
            🔒 Cryptographic Evidence & Audit (Optional)
          </summary>
          <div style="margin-top: 10px; cursor: default;">
            <p style="margin: 0 0 8px 0; font-size: 10px; color: #64748b; line-height: 1.4;">
              Settlement hashes are irreversible SHA-256 HMAC commitments recorded post-expiry to mathematically certify price feed authenticity without revealing internal state.
            </p>
            ${(contract.settlement_evidence_hash || (contract as any).evidence_hash) ? `
              <div style="margin-bottom: 8px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                  <span style="color: #64748b; font-size: 10px; font-weight: 600; text-transform: uppercase;">SHA-256 Digest</span>
                  <span style="background: #05966922; color: #10b981; border: 1px solid #10b98144; font-size: 9px; padding: 1px 5px; border-radius: 4px; font-weight: 700;">VERIFIED</span>
                </div>
                <code style="font-family: 'JetBrains Mono', monospace; font-size: 10.5px; color: #94a3b8; word-break: break-all; display: block;">${contract.settlement_evidence_hash || (contract as any).evidence_hash}</code>
              </div>
            ` : ''}

            ${(contract as any).settlement_signature ? `
              <div style="border-top: 1px solid #1e293b; padding-top: 8px; margin-top: 8px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                  <span style="color: #64748b; font-size: 10px; font-weight: 600; text-transform: uppercase;">Ed25519 Signature</span>
                  <span id="receipt-verify-badge" style="background: #0284c722; color: #38bdf8; border: 1px solid #38bdf844; font-size: 9px; padding: 1px 5px; border-radius: 4px; font-weight: 700;">AUTHENTIC</span>
                </div>
                <code style="font-family: 'JetBrains Mono', monospace; font-size: 10.5px; color: #38bdf8; word-break: break-all; display: block; margin-bottom: 6px;">${(contract as any).settlement_signature}</code>
                ${(contract as any).settlement_signer_key_id ? `<span style="font-size: 10px; color: #64748b; margin-bottom: 8px; display: block;">Signer: ${(contract as any).settlement_signer_key_id}</span>` : ''}
                <div id="receipt-verify-container" style="display: flex; align-items: center; gap: 8px; margin-top: 6px;">
                  <button id="verify-receipt-btn" style="background: rgba(16,185,129,0.15); color: #10b981; border: 1px solid rgba(16,185,129,0.3); padding: 4px 10px; border-radius: 4px; font-size: 10.5px; font-weight: 700; cursor: pointer; transition: background 0.15s;">
                    🛡️ Verify Platform Signature
                  </button>
                  <span id="receipt-verify-msg" style="font-size: 10px; color: #94a3b8;"></span>
                </div>
              </div>
            ` : ''}
          </div>
        </details>
      ` : ''}

      <div style="display: flex; justify-content: flex-end; margin-top: 8px;">
        <button id="close-receipt-btn" style="background: #0284c7; color: #ffffff; border: none; padding: 9px 24px; border-radius: 6px; font-size: 13px; cursor: pointer; font-weight: 700; transition: background 0.15s;">
          Close
        </button>
      </div>
    </div>
  `;
}
